import { useApolloClient, useMutation } from "@apollo/client";
import { Stack } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { gql } from "generated-graphql";
import { ReceivedEmailStatus } from "generated-graphql/graphql";
import { useCallback, useState } from "react";

import { InboundEmailGrid } from "./InboundEmailGrid";
import { IntakeHeader } from "./IntakeHeader";
import { RouteDialog } from "./RouteDialog";
import { SenderDialog } from "./SenderDialog";
import {
  type SenderDialogValues,
  emptySenderValues,
  senderValuesForAddress,
  type InboundEmailRow,
} from "./senderDialogValues";
import { SendersGrid, type SenderListingRow } from "./SendersGrid";
import { useDismissReceivedEmail } from "./useDismissReceivedEmail";

/**
 * Manifest email intake, across every organization.
 *
 * Replaces the per-organization tab this started as. The data was never tenant-scoped:
 * `ReceivedEmail.tenantId` is nullable because resolution cannot attribute mail from a
 * sender nobody allowlists, so half of what the old page showed was environment-wide
 * and had to be labelled as such on a page otherwise about one customer.
 *
 * Mail is first because it is what someone came to look at; the allowlist is second
 * because it is what they change in response.
 */
const TOGGLE_SENDER = gql(`
  mutation ToggleManifestEmailSender($input: UpsertEmailIntakeSenderInput!) {
    upsertEmailIntakeSender(input: $input) {
      id
      enabled
    }
  }
`);

const DELETE_SENDER = gql(`
  mutation DeleteManifestEmailSender($id: ID!) {
    deleteEmailIntakeSender(id: $id)
  }
`);

const REPLAY_EMAIL = gql(`
  mutation ReplayReceivedEmail($id: ID!) {
    replayReceivedEmail(id: $id)
  }
`);

export function ManifestEmail() {
  const apolloClient = useApolloClient();
  const alerts = useAlerts();
  const [dialogValues, setDialogValues] = useState<SenderDialogValues | null>(null);
  const [pendingDelete, setPendingDelete] = useState<SenderListingRow | null>(null);
  const [pendingReplay, setPendingReplay] = useState<InboundEmailRow | null>(null);
  /**
   * The row a re-run was just asked for, with the status it had at the time.
   *
   * The lambda claims the row, not the API, so for a second or two afterwards the row
   * still reads its old status and still offers the same action. Holding the status
   * rather than a timer means the control returns by itself the moment the row moves,
   * and nothing has to guess how long that takes.
   */
  const [busy, setBusy] = useState<{ id: string; status: ReceivedEmailStatus } | null>(null);
  const [pendingRoute, setPendingRoute] = useState<InboundEmailRow | null>(null);
  const [toggleSender] = useMutation(TOGGLE_SENDER);
  const [deleteSender, { loading: deleting }] = useMutation(DELETE_SENDER);
  const [replayEmail, { loading: replaying }] = useMutation(REPLAY_EMAIL);
  const handleDismiss = useDismissReceivedEmail();

  const handleAdd = useCallback(() => setDialogValues(emptySenderValues()), []);

  // From an inbound row: prefills the address, leaving the organization to be chosen.
  // On the old page it was implicitly whichever customer's tab you were on.
  const handleAddSender = useCallback(
    (fromAddress: string) => setDialogValues(senderValuesForAddress(fromAddress)),
    []
  );

  const handleEdit = useCallback(
    (row: SenderListingRow) =>
      setDialogValues({
        id: row.sender.id,
        organization: row.tenant,
        enabled: row.sender.enabled,
        matchType: row.sender.matchType,
        value: row.sender.value,
        includeSubdomains: row.sender.includeSubdomains,
        label: row.sender.label ?? "",
        facility: row.sender.facility ?? null,
      }),
    []
  );

  const refreshBothGrids = useCallback(() => {
    apolloClient.cache.evict({ fieldName: "emailIntakeSenders" });
    apolloClient.cache.evict({ fieldName: "receivedEmails" });
    apolloClient.cache.gc();
  }, [apolloClient]);

  const [pendingDisable, setPendingDisable] = useState<SenderListingRow | null>(null);

  const writeEnabled = useCallback(
    (row: SenderListingRow, enabled: boolean) => {
      void (async () => {
        try {
          // The whole entry goes back, not just `enabled`: the mutation is an upsert,
          // and a partial write would blank the fields it does not mention.
          await toggleSender({
            variables: {
              input: {
                id: row.sender.id,
                tenantId: row.sender.tenantId,
                matchType: row.sender.matchType,
                value: row.sender.value,
                includeSubdomains: row.sender.includeSubdomains,
                label: row.sender.label ?? null,
                facilityId: row.sender.facility?.id ?? null,
                enabled,
              },
            },
          });
          refreshBothGrids();
        } catch (err) {
          alerts.error(err instanceof Error ? err.message : "Couldn't update that sender.", err);
        }
      })();
    },
    [alerts, refreshBothGrids, toggleSender]
  );

  /**
   * Switching one off stops a customer's mail, so it asks first. Switching one on
   * does not, so it does not — and the ticket wants this to stay the cheap
   * alternative to deleting, which is a confirm plus a menu.
   */
  const handleToggle = useCallback(
    (row: SenderListingRow, enabled: boolean) => {
      if (enabled) {
        writeEnabled(row, true);
        return;
      }
      setPendingDisable(row);
    },
    [writeEnabled]
  );

  const confirmDisable = useCallback(async () => {
    if (!pendingDisable) return;
    writeEnabled(pendingDisable, false);
    setPendingDisable(null);
  }, [pendingDisable, writeEnabled]);

  /**
   * The lambda does the work after this returns, so the grid is refreshed for the
   * bookkeeping that changed synchronously and the message says the rest follows.
   * Both inbound queries are evicted: the row's status and the opened detail panel
   * read from different fields of the cache.
   */
  const confirmReplay = useCallback(async () => {
    if (!pendingReplay) return;
    try {
      await replayEmail({ variables: { id: pendingReplay.id } });
      alerts.success("Replay started. The row updates as intake runs; refresh in a moment.");
      setBusy({ id: pendingReplay.id, status: pendingReplay.status });
      setPendingReplay(null);
      apolloClient.cache.evict({ fieldName: "receivedEmails" });
      apolloClient.cache.evict({ fieldName: "receivedEmail" });
      apolloClient.cache.gc();
    } catch (err) {
      alerts.error(err instanceof Error ? err.message : "Couldn't replay that email.", err);
    }
  }, [alerts, apolloClient, pendingReplay, replayEmail]);

  const handleDelete = useCallback(async () => {
    if (!pendingDelete) return;
    try {
      await deleteSender({ variables: { id: pendingDelete.sender.id } });
      alerts.success("Allowed sender removed.");
      setPendingDelete(null);
      refreshBothGrids();
    } catch (err) {
      alerts.error(err instanceof Error ? err.message : "Couldn't remove that sender.", err);
    }
  }, [alerts, deleteSender, pendingDelete, refreshBothGrids]);

  // Both grids read from queries this touches — a new sender changes the senders grid,
  // and can change how an inbound row is attributed — so the cache is evicted rather
  // than refetching one of them and leaving the other stale.

  return (
    // Horizontal padding only. The tab panel already supplies 24px top and bottom and
    // zeroes the sides, so adding `py` here stacked two gaps before the heading. The
    // grids keep `compact` so they do not double-pad horizontally inside this either.
    <Stack spacing={3} sx={{ px: 3 }}>
      <IntakeHeader />

      <InboundEmailGrid
        onAddSender={handleAddSender}
        onReplay={setPendingReplay}
        onRoute={setPendingRoute}
        onDismiss={handleDismiss}
        busy={busy}
      />

      <SendersGrid
        onAdd={handleAdd}
        onEdit={handleEdit}
        onToggle={handleToggle}
        onDelete={setPendingDelete}
      />

      {dialogValues && (
        <SenderDialog
          // Remounts when a different row is opened, so the fields reseed from props.
          key={dialogValues.id ?? "new"}
          initialValues={dialogValues}
          onClose={() => setDialogValues(null)}
          onSaved={refreshBothGrids}
        />
      )}

      {pendingDisable && (
        <ConfirmDialog
          open
          header="Stop accepting mail from this sender?"
          resources={[
            {
              resourceType: "Sender",
              resourceName: `${pendingDisable.sender.value} (${pendingDisable.tenant.name})`,
            },
          ]}
          info="Mail from this sender will be refused from now on, and refused outright rather than handed to whoever holds a broader entry. The entry and its history are kept, so you can switch it back on at any time."
          confirmText="Stop accepting"
          onConfirm={confirmDisable}
          onCancel={() => setPendingDisable(null)}
          onClose={() => setPendingDisable(null)}
        />
      )}

      {pendingRoute && (
        // Keyed per row so the picker reseeds empty when a different email is opened.
        <RouteDialog
          key={pendingRoute.id}
          email={pendingRoute}
          onRouted={() => setBusy({ id: pendingRoute.id, status: pendingRoute.status })}
          onClose={() => setPendingRoute(null)}
        />
      )}

      {pendingReplay && (
        <ConfirmDialog
          open
          loading={replaying}
          header="Replay this email?"
          resources={[
            {
              resourceType: "Email",
              resourceName: `${pendingReplay.subject || "(no subject)"} · ${
                pendingReplay.fromAddress ?? "unknown sender"
              }`,
            },
          ]}
          info="Intake runs again from the stored message: the sender is checked against the allowlist as it stands now, any attachments are filed, and if anything files the sender receives the usual confirmation. No reply was sent the first time, so this will be their first."
          confirmText="Replay"
          onConfirm={confirmReplay}
          onCancel={() => setPendingReplay(null)}
          onClose={() => setPendingReplay(null)}
        />
      )}

      {pendingDelete && (
        <ConfirmDialog
          open
          loading={deleting}
          header="Remove allowed sender?"
          resources={[
            {
              resourceType: "Sender",
              resourceName: `${pendingDelete.sender.value} (${pendingDelete.tenant.name})`,
            },
          ]}
          info="Manifests emailed from this sender will be refused from now on. To pause it temporarily instead, turn Enabled off — that keeps the entry and its history."
          confirmText="Remove"
          onConfirm={handleDelete}
          onCancel={() => setPendingDelete(null)}
          onClose={() => setPendingDelete(null)}
        />
      )}
    </Stack>
  );
}
